import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import AuthNavbar from "./AuthNavbar";
import LoadingState from "../Common/LoadingState";
import { validatePhone, formatPhone, validateIndianPhone, formatIndianPhone } from "../../utils/validation";
import PhoneInput from "../Common/PhoneInput";
import { sendPhoneVerificationOTP, verifyPhoneOTP, resendPhoneVerificationOTP, getVerificationStatus } from "../../services/auth";
import "./Auth.css";

interface PhoneVerificationProps {
  onVerificationSuccess?: () => void;
  onBackToEmail?: () => void;
}

const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  onVerificationSuccess,
  onBackToEmail,
}) => {
  const navigate = useNavigate();
  
  // Form states
  const [phone, setPhone] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  
  // UI states
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  
  // Verification status states
  const [emailVerified, setEmailVerified] = useState<boolean>(false);
  const [canAccessDashboard, setCanAccessDashboard] = useState<boolean>(false);
  
  // Validation states
  const [phoneValidationMsg, setPhoneValidationMsg] = useState<string | null>(null);
  
  // OTP input refs for auto-focus
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  // Initialize OTP input refs
  useEffect(() => {
    otpInputRefs.current = otpInputRefs.current.slice(0, 6);
  }, []);

  // Check verification status on component mount
  useEffect(() => {
    let isMounted = true;
    
    const checkVerificationStatus = async () => {
      try {
        const email = localStorage.getItem("email");
        const phone = localStorage.getItem("phone");
        
        if (!email) {
          // No email found, redirect to login
          if (isMounted) navigate("/login");
          return;
        }

        // Check verification status
        const statusResult = await getVerificationStatus(email, phone || undefined);
        
        if (!isMounted) return; // Component unmounted, ignore result
        
        if (statusResult.success && statusResult.data) {
          const { email_verified, phone_verified, can_access_dashboard } = statusResult.data;
          
          setEmailVerified(email_verified);
          setCanAccessDashboard(can_access_dashboard);
          
          if (!email_verified) {
            // Email not verified, redirect to email verification
            navigate("/email-verification");
            return;
          }
          
          if (phone_verified && can_access_dashboard) {
            // Already fully verified, redirect to dashboard
            // Use replace: true to avoid stacking history and ensure layout reload
            navigate("/dashboard", { replace: true });
            return;
          }
          
          // Email verified but phone not verified - show phone verification
          // This is the correct state to be on this page
        } else {
          // Error checking status, redirect to login
          navigate("/login");
          return;
        }
      } catch (error) {
        console.error("Error checking verification status:", error);
        if (isMounted) navigate("/login");
        return;
      } finally {
        if (isMounted) setInitialLoading(false);
      }
    };

    checkVerificationStatus();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []); // Only run once on mount

  // Auto-focus first OTP input when step changes to OTP
  useEffect(() => {
    if (step === 'otp' && otpInputRefs.current[0]) {
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 100);
    }
  }, [step]);

  // Show loading screen while checking initial verification status
  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)]">
        <LoadingState message="Checking verification status..." fullPage={true} size="large" />
      </div>
    );
  }

  // Show error if email not verified (shouldn't happen due to redirect, but safety check)
  if (!emailVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)]">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-[color:var(--foreground)] mb-4">
            Email Verification Required
          </h2>
          <p className="text-[color:var(--muted-foreground)] mb-6">
            Please verify your email address before proceeding with phone verification.
          </p>
          <Link
            to="/email-verification"
            className="inline-block bg-[color:var(--accent)] text-white px-6 py-2 rounded-md hover:bg-[color:var(--accent)]/90 transition-colors"
          >
            Go to Email Verification
          </Link>
        </div>
      </div>
    );
  }

  // Validate phone number on blur
  const validatePhoneOnBlur = () => {
    setPhoneValidationMsg(null);

    if (!phone.trim()) {
      setPhoneValidationMsg("Phone number is required");
      return;
    }

    if (!validatePhone(phone)) {
      setPhoneValidationMsg("Please enter a valid phone number");
      return;
    }
  };

  // Handle phone number submission
  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!phone.trim()) {
      setError("Please enter your phone number");
      return;
    }

    if (!validatePhone(phone)) {
      setError("Please enter a valid phone number");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formattedPhone = formatPhone(phone);
      
      const result = await sendPhoneVerificationOTP(formattedPhone);
      
      if (result.success) {
        setSuccess(result.message || "OTP sent successfully!");
        setStep('otp');
      } else {
        setError(result.error || "Failed to send OTP. Please try again.");
      }
    } catch (err) {
      setError("Failed to send OTP. Please try again.");
      console.error("Phone OTP request error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input change
  const handleOtpChange = (index: number, value: string) => {
    // Only allow numeric input
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = otp.split("");
    newOtp[index] = value;
    
    // Update OTP state
    const updatedOtp = newOtp.join("");
    setOtp(updatedOtp);
    
    // Auto-focus next input
    if (value && index < 5 && otpInputRefs.current[index + 1]) {
      otpInputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all 6 digits are entered
    if (updatedOtp.length === 6) {
      handleOtpSubmit(updatedOtp);
    }
  };

  // Handle backspace in OTP input
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste in OTP input
  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    
    if (pastedData.length > 0) {
      setOtp(pastedData.padEnd(6, ""));
      
      // Focus the next empty input or the last input
      const nextIndex = Math.min(pastedData.length, 5);
      otpInputRefs.current[nextIndex]?.focus();
      
      // Auto-submit if 6 digits pasted
      if (pastedData.length === 6) {
        handleOtpSubmit(pastedData);
      }
    }
  };

  // Handle OTP form submission
  const handleOtpSubmit = async (otpCode?: string) => {
    const codeToVerify = otpCode || otp;
    
    if (codeToVerify.length !== 6) {
      setError("Please enter the complete 6-digit code");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formattedPhone = formatPhone(phone);
      
      const result = await verifyPhoneOTP(formattedPhone, codeToVerify);
      
      if (result.success) {
        setSuccess("Phone verified successfully! Redirecting...");
        
        if (onVerificationSuccess) {
          onVerificationSuccess();
        }
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      } else {
        setError(result.error || "Invalid or expired code. Please try again.");
        // Clear OTP on error
        setOtp("");
        if (otpInputRefs.current[0]) {
          otpInputRefs.current[0].focus();
        }
      }
    } catch (err) {
      setError("Invalid or expired code. Please try again.");
      console.error("Phone OTP verification error:", err);
      // Clear OTP on error
      setOtp("");
      if (otpInputRefs.current[0]) {
        otpInputRefs.current[0].focus();
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle manual OTP form submission
  const handleOtpFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleOtpSubmit();
  };

  // Handle resend OTP
  const handleResendOTP = async () => {
    setResendLoading(true);
    setError(null);

    try {
      const formattedPhone = formatPhone(phone);
      
      const result = await resendPhoneVerificationOTP(formattedPhone);
      
      if (result.success) {
        setSuccess(result.message || "New OTP sent successfully!");
        // Clear current OTP
        setOtp("");
        if (otpInputRefs.current[0]) {
          otpInputRefs.current[0].focus();
        }
      } else {
        setError(result.error || "Failed to resend OTP. Please try again.");
      }
    } catch (err) {
      setError("Failed to resend OTP. Please try again.");
      console.error("Resend phone OTP error:", err);
    } finally {
      setResendLoading(false);
    }
  };

  // Handle back to phone number
  const handleBackToPhone = () => {
    setStep('phone');
    setOtp("");
    setError(null);
    setSuccess(null);
  };

  // Render phone number input step
  const renderPhoneStep = () => {
    return (
      <>
        <AuthNavbar />
        <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
          <motion.div
            className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            {/* Back button */}
            {onBackToEmail && (
              <div className="flex justify-start mb-4">
                <button
                  onClick={onBackToEmail}
                  className="flex items-center text-sm text-[color:var(--foreground)] hover:text-[color:var(--color-secondary)] transition-colors px-3 py-1.5 rounded-full border border-[color:var(--border)] bg-[color:var(--card)] hover:bg-[color:var(--muted)]/10"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mr-1"
                  >
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                  Back to Email Verification
                </button>
              </div>
            )}

            <div className="flex flex-col items-center mb-8">
              <motion.div
                className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <motion.svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-green-600"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </motion.svg>
              </motion.div>

              <motion.h2
                className="text-3xl pt-2 font-bold text-[color:var(--foreground)]"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Verify Phone Number
              </motion.h2>
              <motion.p
                className="text-sm text-[color:var(--muted-foreground)] mt-1 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                Enter your phone number to complete registration and access your dashboard
              </motion.p>
            </div>

            <motion.form
              onSubmit={handlePhoneSubmit}
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-[color:var(--foreground)]"
                >
                  Phone Number
                </label>
                <PhoneInput
                  id="phone"
                  value={phone}
                  onChange={(value) => setPhone(value || "")}
                  onBlur={validatePhoneOnBlur}
                  placeholder="Enter your phone number"
                  autoComplete="tel"
                  disabled={loading}
                  className="mt-1 block w-full"
                  error={!!phoneValidationMsg}
                />
                {phoneValidationMsg && (
                  <motion.p
                    className="mt-1 text-sm text-red-600"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {phoneValidationMsg}
                  </motion.p>
                )}
              </motion.div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Each phone number can only be used for one account. 
                  If this number is already registered, you'll see an error message.
                </p>
              </div>

              {error && (
                <motion.div
                  className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <p>{error}</p>
                  {error.includes("already") && (
                    <p className="mt-2 text-xs">
                      If you believe this is an error, please contact support.
                    </p>
                  )}
                </motion.div>
              )}

              {success && (
                <motion.div
                  className="p-3 bg-green-50 border border-green-200 rounded-md text-sm"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0, scale: [1, 1.03, 1] }}
                  transition={{
                    duration: 0.4,
                    scale: { duration: 0.5, times: [0, 0.5, 1] },
                  }}
                >
                  <div className="flex items-center">
                    <div className="shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                      <motion.svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-green-600"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                      >
                        <path d="M20 6L9 17l-5-5"></path>
                      </motion.svg>
                    </div>
                    <span className="text-green-600 font-medium">{success}</span>
                  </div>
                </motion.div>
              )}

              <motion.button
                type="submit"
                disabled={loading}
                className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                {loading ? "Sending OTP..." : "Send OTP"}
              </motion.button>
            </motion.form>

            <motion.div
              className="mt-6 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7 }}
            >
              <Link
                to="/login"
                className="text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] font-medium"
              >
                ← Back to login
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </>
    );
  };
 
 // Render OTP verification step
  const renderOtpStep = () => {
    return (
      <>
        <AuthNavbar />
        <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
          <motion.div
            className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            {/* Back button */}
            <div className="flex justify-start mb-4">
              <button
                onClick={handleBackToPhone}
                className="flex items-center text-sm text-[color:var(--foreground)] hover:text-[color:var(--color-secondary)] transition-colors px-3 py-1.5 rounded-full border border-[color:var(--border)] bg-[color:var(--card)] hover:bg-[color:var(--muted)]/10"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-1"
                >
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                Back to Phone Number
              </button>
            </div>

            <div className="flex flex-col items-center mb-8">
              <motion.div
                className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <motion.svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-blue-600"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </motion.svg>
              </motion.div>

              <motion.h2
                className="text-3xl pt-2 font-bold text-[color:var(--foreground)]"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Enter Verification Code
              </motion.h2>
              <motion.p
                className="text-sm text-[color:var(--muted-foreground)] mt-1 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                We've sent a 6-digit code to <strong>{phone}</strong>
              </motion.p>
            </div>

            <motion.form
              onSubmit={handleOtpFormSubmit}
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              {/* OTP Input */}
              <motion.div
                className="flex justify-center space-x-2"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    ref={(el) => (otpInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={otp[index] || ""}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    disabled={loading}
                    className="w-12 h-12 text-center text-lg font-semibold bg-transparent border-2 border-[color:var(--border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[color:var(--color-secondary)] focus:border-transparent text-[color:var(--foreground)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  />
                ))}
              </motion.div>

              {error && (
                <motion.div
                  className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <p>{error}</p>
                  {error.includes("already") && (
                    <p className="mt-2 text-xs">
                      This phone number is already associated with another account. Please use a different number or contact support.
                    </p>
                  )}
                </motion.div>
              )}

              {success && (
                <motion.div
                  className="p-3 bg-green-50 border border-green-200 rounded-md text-sm"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0, scale: [1, 1.03, 1] }}
                  transition={{
                    duration: 0.4,
                    scale: { duration: 0.5, times: [0, 0.5, 1] },
                  }}
                >
                  <div className="flex items-center">
                    <div className="shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                      <motion.svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-green-600"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                      >
                        <path d="M20 6L9 17l-5-5"></path>
                      </motion.svg>
                    </div>
                    <span className="text-green-600 font-medium">{success}</span>
                  </div>
                </motion.div>
              )}

              <motion.button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                {loading ? "Verifying..." : "Verify Code"}
              </motion.button>

              <motion.div
                className="text-center space-y-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <p className="text-sm text-[color:var(--muted-foreground)]">
                  Didn't receive the code?
                </p>
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={resendLoading}
                  className="text-sm text-[color:var(--color-secondary)] hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendLoading ? "Sending..." : "Resend Code"}
                </button>
              </motion.div>
            </motion.form>

            <motion.div
              className="mt-6 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              <Link
                to="/login"
                className="text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] font-medium"
              >
                ← Back to login
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </>
    );
  };

  // Render appropriate step
  return step === 'phone' ? renderPhoneStep() : renderOtpStep();
};

export default PhoneVerification;